import { Component, inject, TemplateRef, ViewChild } from '@angular/core';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LookupService } from '../../../../services/lookup.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { ListService } from '../../../../shared/services/list.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { ListTableComponent } from '@ppl/ui';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { CommonModule } from '@angular/common';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { DatePickerModule } from 'primeng/datepicker';
import { UserService } from '../../../../services/user.service';
import { InvoiceFilterComponent } from '../staff/invoices-staff-list/invoice-filter/invoice-filter.component';
import { InvoiceAdjustVoidEntryComponent } from '../invoice-adjust-void-entry/invoice-adjust-void-entry.component';
import { AuthorizationDetailsComponent } from '../../authorizations/authorization-details/authorization-details.component';
import { SectionsService } from '../../../../services/sections.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { EditpdfComponent } from '../../../editpdf/editpdf.component';
import { PDFFormConfig } from '../../../../interfaces/config';


@Component({
    selector: 'ppl-invoice-adjustment',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        NgZorroModule,
        NzDatePickerModule,
        DatePickerModule,
        ListTableComponent,
        InvoiceFilterComponent,
        InvoiceAdjustVoidEntryComponent,
        AuthorizationDetailsComponent,
        EditpdfComponent,
    ],
    templateUrl: './invoice-adjustment.component.html',
    styleUrl: './invoice-adjustment.component.css'
})
export class InvoiceAdjustmentComponent {
    @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
    @ViewChild(InvoiceFilterComponent) filterChildComponent!: InvoiceFilterComponent;
    @ViewChild('timesheetAdjustVoidDialog') timesheetAdjustVoidDialog!: TemplateRef<any>;
    @ViewChild('searchFilterWarningDialog') searchFilterWarningDialog!: TemplateRef<any>;
    @ViewChild('authDialog') authDialog!: TemplateRef<any>;
    // Passed to ppl-authorization-details as an input; never put into the shared selected lead.
    selectedAuthorizationId: any = null;
    @ViewChild('pdfDialog') pdfDialogRef!: TemplateRef<any>;

    private dialogSvc = inject(DialogService);
    private allowListStore = inject(AllowListAuthStore);

    // ─── Page Config ────────────────────────────────────────────────────────────
    pageId = PAGE_CONSTANTS.STAFF_PAYROLL_ADJUSTMENT;          // ← update PAGE_CONSTANTS accordingly
    pageName = 'invoiceAdjustmentEntry';               // ← invoice-specific API pageName
    pageTitle = 'Invoice Adjustment';
    dialogSource = 'invoice';
    // ────────────────────────────────────────────────────────────────────────────

    isReadOnly = false;
    actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
    appliedFilters: any;
    selectedAction: any;
    programName!: string;
    processingStatusList: any;
    businessStatusList: any;
    timesheets: any[] = [];
    hasSearched = false;
    serviceCodes: LookupValueItem[] = [];
    timesheetData: any = {};
    adjustVoidDialog: any;
    chequeNumbers: any[] = [];

    // Paystub viewer state
    showPdfModal = false;
    pdfConfig?: PDFFormConfig;
    pdfFile?: string;
    pdfBlob?: Blob;
    listReloading = false;

    // Invoice-specific button labels
    buttons = [
        {
            button_id: 'Adjust',
            button_label: 'Adjust',
            buttonAction: 'adjustTimesheet',
            className: 'primary-btn-color'
        },
        // {
        //     button_id: 'AdjustAll',
        //     button_label: 'AdjustAll',
        //     buttonAction: 'adjustAllTimesheet',
        //     className: 'primary-btn-color'
        // },
        {
            button_id: 'Void',
            button_label: 'Void',
            buttonAction: 'voidTimesheet',
            className: 'primary-btn-color'
        }
        // {
        //     button_id: 'VoidAll',
        //     button_label: 'VoidAll',
        //     buttonAction: 'voidAllTimesheets',
        //     className: 'primary-btn-color'
        // }
    ];

    cols = [
        { field: 'serviceDate', header: 'Service \nDate', jsonpath: 'serviceDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'serviceCodeWithDesc', header: 'Service \nCode', jsonpath: 'serviceCodeWithDesc', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'authDesc', header: 'Service \nDescription', jsonpath: 'authDesc', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'consumerName', header: 'Participant \nName', jsonpath: 'consumerName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'consumerPplId', header: 'Participant \nPPL ID', jsonpath: 'consumerPplId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
        { field: 'providerName', header: 'Provider \nName', jsonpath: 'providerName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'providerPplId', header: 'Provider \nPPL ID', jsonpath: 'providerPplId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
        { field: 'invoiceNumber', header: 'Invoice \nNumber', jsonpath: 'invoiceNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'invoiceDate', header: 'Invoice \nDate', jsonpath: 'invoiceDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'billedUnits', header: 'Billed \nUnits', jsonpath: 'billedUnits', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'paidUnits', header: 'Paid \nUnits', jsonpath: 'paidUnits', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'unitPrice', header: 'Unit \nPrice', jsonpath: 'unitPrice', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'totalCost', header: 'Total \nCost', jsonpath: 'totalCost', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'businessStatus', header: 'Business \nStatus', jsonpath: 'businessStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'processingStatus', header: 'Processing \nStatus', jsonpath: 'processingStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        // { field: 'sourceTypeLkp', header: 'Source', jsonpath: 'sourceTypeLkp', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'mcoName', header: 'Regional \nCenter', jsonpath: 'mcoName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'tasAuthorizationDetailId', header: 'Authorization \nNumber', jsonpath: 'tasAuthorizationDetailId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
        { field: 'chequeNumber', header: 'Check \nNumber', jsonpath: 'chequeNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    ];

    fields = [
        { field: 'Timeframe', show: true, default: '30 days' },
        { field: 'serviceDate', show: true, hideFuturePeriods: true },
        { field: 'consumer', show: true },
        { field: 'consumerPplId', show: true },
        { field: 'provider', show: true },
        { field: 'providerPplId', show: true },
        { field: 'serviceCode', show: true },
        { field: 'chequeNumber', show: true },
        { field: 'servicePeriod', show: true, hideFuturePeriods: true },
        { field: 'payPeriod', show: true, hideFuturePeriods: true }
    ];

    // Only keys relevant to the 3 active filters
    keyMapping: { [key: string]: string } = {
        serviceDate: 'serviceDate',       // ← keep for invoice
        Timeframe: 'serviceDate',
        consumer: 'consumerName',
        consumerPplId: 'consumerPplId',
        provider: 'providerName',
        providerPplId: 'providerPplId',
        serviceCode: 'serviceCode',
        servicePeriod: 'servicePeriodId',
        payPeriod: 'payPeriodId',
        chequeNumber: 'chequeNumber',
        crmNumber: 'crmNumber'
    };

    onViewUserClicked = (payload: any) => {
        this.userService.setViewUserEvent({
            userId: payload.userId,
            isConsumer: payload.isConsumer
        });
    };

    constructor(
        private listService: ListService,
        private fb: FormBuilder,
        private lookupService: LookupService,
        private dateTimeFormatterService: DateTimeFormatterService,
        private userService: UserService,
        private errorAlertService: ErrorAlertService,
        private sectionsService: SectionsService,
        private timesheetService: TimesheetService
    ) { }

    ngOnInit(): void {
        const selectedProgram = this.userService.getSelectedProgram();
        this.programName = selectedProgram?.program_code ?? '';
        this.businessStatusList = this.lookupService.getLookupValue('Business Status');
        this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
        this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');
        this.setPageAccess();
    }

    private setPageAccess(): void {
        const authConfig = this.allowListStore.getConfig();
        if (authConfig && !authConfig.pages[this.pageId]) {
            this.isReadOnly = true;
        }
    }

    // Destroys + recreates the results table so the lazy loadPage grid fully resets to the
    // latest filter results, then reloads.
    private reloadGrid(filters: any): void {
        this.listReloading = true;
        this.timesheets = [];
        setTimeout(() => {
            this.listReloading = false;
            this.searchTimesheets(filters);
        });
    }

    // ─── Search & Data ───────────────────────────────────────────────────────────

    searchTimesheets(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false, recordsPerPage = 20): void {
        this.hasSearched = true;

        const processedPayload = this.buildRequestPayload(
            this.pageName,
            'serviceDate',
            filters,
            startingPageNumber,
            recordsPerPage
        );

        this.listService.getListData(processedPayload, 'claims').subscribe((data: any) => {
            const records = this.processData(data.responsedata);
            if (isChequeLookUp) {
                if (records?.length) {
                    this.chequeNumbers = Array.from(
                        new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
                }
            } else {
                this.timesheets = startingPageNumber > 1
                    ? [...this.timesheets, ...records]
                    : records;
                if (this.timesheets?.length) {
                    this.chequeNumbers = Array.from(
                        new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
                }
            }
        });
    }

    buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20): any {
        if (!searchParams.length) return;

        return {
            pageName,
            programCode: this.programName,
            condition: 'and',
            recordsPerPage,
            startingPageNumber,
            searchParam: searchParams,
            orderByColumn: orderBy ? [orderBy] : [],
            orderBy: ['desc']
        };
    }

    processData(data: any): any {
        if (data?.length > 0) {
            data.forEach((element: any) => {
                element.timeInTimeOut =
                    this.dateTimeFormatterService.convertTo12HourFormat(element.timeIn) +
                    ' - ' +
                    this.dateTimeFormatterService.convertTo12HourFormat(element.timeOut);

                element.businessStatus = this.businessStatusList
                    .filter((s: any) => s.lkpValueCode === element.businessStatusLkp)
                    .map((s: any) => s.lkpValueName);

                element.processingStatus = this.processingStatusList
                    .filter((s: any) => s.lkpValueCode === element.processingStatusLkp)
                    .map((s: any) => s.lkpValueName);

                element.serviceCodeWithDesc = [element.serviceCode, element.serviceDescription]
                    .filter((part: any) => part != null && String(part).trim() !== '')
                    .join(' - ');
            });
        }
        return data;
    }

    onCellClicked(event: any): void {
        const col = event?.col;
        const rowData = event?.rowData;
        if (col?.field === 'tasAuthorizationDetailId' && rowData?.tasAuthorizationDetailId) {
            this.openAuthDialog(rowData);
        }
    }

    openAuthDialog(rowData: any): void {
        this.selectedAuthorizationId = rowData.tasAuthorizationDetailId;
        const dialogConfig: DialogConfig = {
            title: `Auth Details for ${rowData.tasAuthorizationDetailId}`,
            size: 'xx-large',
            position: 'top',
            showClose: true,
            showButtons: false,
        };
        this.dialogSvc.openDialog(this.authDialog, dialogConfig, () => { }, () => { });
    }

    // ─── Paystub view / download ────────────────────────────────────────────────

    onRowActions(event: any): void {
        if (event?.type === 'download') {
            this.timesheetService.downloadPaystub(event, this.programName);
        } else {
            this.viewPaystub(event);
        }
    }

    private viewPaystub(event: any): void {
        this.showPdfModal = true;
        this.pdfConfig = {
            form_id: '',
            is_mandatory: false,
            form_description: '',
            form_name: '',
            fields: []
        };

        const dialogConfig: DialogConfig = {
            title: 'Paystub',
            size: 'xx-large',
            position: 'top',
            showClose: true,
            showButtons: false,
        };
        this.dialogSvc.openDialog(this.pdfDialogRef, dialogConfig, () => this.handlePdfDialogCancel(), () => this.handlePdfDialogCancel());

        this.getPaystub(event?.event?.checkId, event?.event?.chequeNumber, event?.event?.providerPplId);
    }

    private getPaystub(checkId: string, chequeNumber: string, empId: string): void {
        this.timesheetService.getPayStub(checkId, chequeNumber, empId, this.programName).subscribe(response => {
            if (response.body) {
                const blob = new Blob([response.body], { type: response.body.type });
                this.pdfBlob = blob;
                this.pdfFile = window.URL.createObjectURL(blob);
            }
        });
    }

    handlePdfDialogCancel(): void {
        this.showPdfModal = false;
        this.pdfConfig = undefined;
        this.pdfFile = undefined;
        this.pdfBlob = undefined;
    }

    // ─── Filter / Search Handler ─────────────────────────────────────────────────

    onSearch(event: any): void {
        const filters: any[] = [];

        if (event.Timeframe) {
            filters.push({
                jsonPath: 'serviceDate',
                value: event.Timeframe,
                operator: 'greaterThan'
            });
        }

        if (event.serviceDate) {                                          // ← add this block
            filters.push({
                jsonPath: 'serviceDate',
                value: this.dateTimeFormatterService.formatDateToString(event.serviceDate as Date),
                operator: 'Equal'
            });
        }
        if (event.consumer?.trim()) {
            filters.push({ jsonPath: 'consumerName', value: event.consumer.trim(), operator: 'equal' });
        }
        if (event.consumerPplId?.trim()) {
            filters.push({ jsonPath: 'consumerPplId', value: event.consumerPplId.trim(), operator: 'Equal' });
        }
        if (event.provider?.trim()) {
            filters.push({ jsonPath: 'providerName', value: event.provider.trim(), operator: 'equal' });
        }
        if (event.providerPplId?.trim()) {
            filters.push({ jsonPath: 'providerPplId', value: event.providerPplId.trim(), operator: 'Equal' });
        }
        if (event.serviceCode) {
            filters.push({ jsonPath: 'serviceCode', value: event.serviceCode, operator: 'Equal' });
        }
        if (event.servicePeriod) {
            filters.push({ jsonPath: 'servicePeriodId', value: event.servicePeriod, operator: 'Equal' });
        }
        if (event.payPeriod) {
            filters.push({ jsonPath: 'payPeriodId', value: event.payPeriod, operator: 'Equal' });
        }
        if (event.crmNumber) {
            filters.push({ jsonPath: 'crmNumber', value: event.crmNumber, operator: 'Equal' });
        }
        if (event.chequeNumber) {
            // Backend 'In' operator expects values: [...]. Normalize the input — array, single string,
            // or comma-separated string — into a clean string[].
            const chequeValues: string[] = Array.isArray(event.chequeNumber)
                ? event.chequeNumber.map((v: any) => String(v).trim()).filter(Boolean)
                : String(event.chequeNumber).split(',').map(v => v.trim()).filter(Boolean);

            if (chequeValues.length) {
                filters.push({ jsonPath: 'chequeNumber', values: chequeValues, operator: 'In' });
            }
        }

        filters.push({ jsonPath: 'businessStatusLkp', value: 'P', operator: 'Equal' });
        this.appliedFilters = filters;
        this.reloadGrid(filters);
    }

    // ─── Grid Actions ────────────────────────────────────────────────────────────

    gridAction(event: any): void {
        const { button, selectedCheckBox } = event;

        if (!this.isFilterValid(this.filterChildComponent.invoiceSearchForm.value, this.appliedFilters)) {
            this.errorAlertService.openModal('error', 'Error',
                'Some filters are newly applied but the search is not performed. Kindly perform search with updated filter or remove the filter.');
            return;
        }

        const isSingleAction = button.buttonAction === 'adjustTimesheet' || button.buttonAction === 'voidTimesheet';

        if (selectedCheckBox?.length === 0) {
            this.errorAlertService.openModal('error', 'Error', 'Kindly select checkbox and then perform this action.');
            return;
        }

        if (isSingleAction && selectedCheckBox.length > 1) {
            this.errorAlertService.openModal('error', 'Error', 'Adjust / Void operation can be performed on single invoice at a time.');
            return;
        }

        switch (button.buttonAction) {
            case 'adjustTimesheet': {
                const ts = selectedCheckBox[0];
                const selectedService = this.serviceCodes?.find(s => s.lkpValueCode === ts.serviceCodeDetailId.toString());
                this.timesheetData = {
                    invoiceLineId: ts.invoiceLineId,
                    serviceDate: ts.serviceDate,
                    serviceCode: selectedService,
                    authDesc: ts.authDesc,
                    tasAuthorizationDetailId: ts.tasAuthorizationDetailId,
                    billedUnits: ts.billedUnits,
                    providerPplID: ts.providerPplID,
                    participantId: ts.participantId,
                    providerId: ts.providerId,
                    unitPrice: ts.unitPrice,
                    crmNumber: ts.crmNumber,
                };
                break;
            }
            case 'voidTimesheet': {
                this.timesheetData = { invoiceLineId: selectedCheckBox[0].invoiceLineId };
                break;
            }
            // case 'adjustAllTimesheet':
            // case 'voidAllTimesheets': {
            //     if (selectedCheckBox.length > 0) {
            //         this.timesheetData = { invoiceLineId: selectedCheckBox.map((e: any) => e.invoiceLineId) };
            //     }
            //     break;
            // }
        }

        this.selectedAction = button;

        const dialogConfig: DialogConfig = {
            title: `Invoice ${button.button_label}`,
            size: 'medium',
            position: 'top',
            showClose: true,
            showButtons: false,
            styleClass: this.selectedAction?.buttonAction !== 'adjustTimesheet' ? 'limit-dialog-width' : ''
        };

        this.adjustVoidDialog = this.dialogSvc.openDialog(
            this.timesheetAdjustVoidDialog,
            dialogConfig,
            () => this.handleCancel(),
            () => this.handleCancel()
        );
    }

    // ─── Dialog Helpers ──────────────────────────────────────────────────────────

    closeAdjustVoidDialog(): void {
        this.timesheetData = {};
        if (this.adjustVoidDialog) {
            this.dialogSvc.closeDialog(this.adjustVoidDialog);
            this.adjustVoidDialog = null;
        }
    }

    handleCancel(): void {
        this.closeAdjustVoidDialog();
    }

    timesheetAdjustedOrVoided(event: any): void {
        this.closeAdjustVoidDialog();
        this.searchTimesheets(this.appliedFilters, event.startingPageNumber, event?.first ?? 0);
    }

    onPageLoad(event: any): void {
        this.searchTimesheets(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, event.rows ?? 20);
    }

    // ─── Filter Validation Helpers ───────────────────────────────────────────────

    areArraysEqual(a: any[], b: any[]): boolean {
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        if (a.length !== b.length) return false;
        return a.every((val, i) => val === b[i]);
    }

    isFilterValid(timesheetFilterValues: any, searchCallbackFilterValues: any): boolean {
        const activeFilters = Object.keys(timesheetFilterValues).filter(key => {
            const value = timesheetFilterValues[key];
            if (value === null || value === undefined) return false;
            if (typeof value === 'string' && value.trim() === '') return false;
            if (Array.isArray(value) && value.length === 0) return false;
            return true;
        });

        const userAppliedFilters = searchCallbackFilterValues.filter(
            (filter: any) => !['businessStatusLkp', 'serviceDuration'].includes(filter.jsonPath)
        );

        if (activeFilters.length !== userAppliedFilters.length) return false;

        return Object.entries(timesheetFilterValues)
            .filter(([_, value]) => value !== null && value !== '')
            .every(([key, value]: [any, any]) => {
                const mappedKey = this.keyMapping[key] || key;
                if (Array.isArray(value) && value.length === 0) return true;

                let comparisonValue: any;
                if (key === 'Timeframe') {
                    comparisonValue = value;
                } else if (mappedKey === 'serviceDate') {
                    comparisonValue = this.dateTimeFormatterService.formatDateToString(value as Date);
                } else if (mappedKey === 'serviceDuration') {
                    comparisonValue = value.substr(0, value.indexOf(' '));
                } else if (mappedKey === 'chequeNumber') {
                    // Mirror onSearch: the applied filter stores cheque numbers as a trimmed
                    // string[] under `values`. Normalize the form value the same way, otherwise
                    // the array compare fails on string-vs-number or scalar-vs-array.
                    comparisonValue = (Array.isArray(value) ? value : String(value).split(','))
                        .map((v: any) => String(v).trim())
                        .filter(Boolean);
                } else {
                    comparisonValue = value;
                }

                return searchCallbackFilterValues.some((filter: any) =>
                    filter.jsonPath === mappedKey &&
                    (Array.isArray(filter.value || filter.values)
                        ? this.areArraysEqual(filter.values, comparisonValue)
                        : typeof filter?.value === 'string'
                            ? filter?.value?.trim() === comparisonValue?.trim()
                            : filter?.value === comparisonValue)
                );
            });
    }
}
